/**
 * useListing Hook
 * Custom hook for managing listing operations
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { CreateListingRequest, ListingFilter } from '@/types/listing';
import { listingsAPI } from '@/lib/api/listings';
import { PaginationParams } from '@/types/common';

export const useListing = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Helper to handle abort logic
  const createNewSignal = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const getAll = useCallback(
    async (filters?: Partial<ListingFilter & PaginationParams>) => {
      const signal = createNewSignal();
      setLoading(true);
      setError(null);
      try {
        const result = await listingsAPI.getAll(filters);
        if (signal.aborted) return;
        return result;
      } catch (err) {
        if (signal.aborted) return;
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch listings';
        setError(errorMessage);
        throw err;
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    },
    [createNewSignal]
  );

  const getById = useCallback(async (id: string) => {
    const signal = createNewSignal();
    setLoading(true);
    setError(null);
    try {
      const result = await listingsAPI.getById(id);
      if (signal.aborted) return;
      return result;
    } catch (err) {
      if (signal.aborted) return;
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch listing';
      setError(errorMessage);
      throw err;
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, [createNewSignal]);

  const create = useCallback(async (data: CreateListingRequest) => {
    const signal = createNewSignal();
    setLoading(true);
    setError(null);
    try {
      const result = await listingsAPI.create(data);
      if (signal.aborted) return;
      return result;
    } catch (err) {
      if (signal.aborted) return;
      const errorMessage = err instanceof Error ? err.message : 'Failed to create listing';
      setError(errorMessage);
      throw err;
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, [createNewSignal]);

  const update = useCallback(async (id: string, data: Partial<CreateListingRequest>) => {
    const signal = createNewSignal();
    setLoading(true);
    setError(null);
    try {
      const result = await listingsAPI.update(id, data);
      if (signal.aborted) return;
      return result;
    } catch (err) {
      if (signal.aborted) return;
      const errorMessage = err instanceof Error ? err.message : 'Failed to update listing';
      setError(errorMessage);
      throw err;
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, [createNewSignal]);

  const deleteListing = useCallback(async (id: string) => {
    const signal = createNewSignal();
    setLoading(true);
    setError(null);
    try {
      const result = await listingsAPI.delete(id);
      if (signal.aborted) return;
      return result;
    } catch (err) {
      if (signal.aborted) return;
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete listing';
      setError(errorMessage);
      throw err;
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, [createNewSignal]);

  const search = useCallback(
    async (query: string, filters?: Partial<ListingFilter & PaginationParams>) => {
      const signal = createNewSignal();
      setLoading(true);
      setError(null);
      try {
        const result = await listingsAPI.search(query, filters);
        if (signal.aborted) return;
        return result;
      } catch (err) {
        if (signal.aborted) return;
        const errorMessage = err instanceof Error ? err.message : 'Failed to search listings';
        setError(errorMessage);
        throw err;
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    },
    [createNewSignal]
  );

  return {
    loading,
    error,
    getAll,
    getById,
    create,
    update,
    delete: deleteListing,
    search,
  };
};

